import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../auth/authContext.js';
import { useDetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { TabNav } from '../../components/ui/TabNav.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { useBackNavigation } from '../../utils/navigation.js';
import { formatDate } from '../../utils/date.js';
import { AGREEMENT_TYPE_LABELS, agreementPortalBillingMode } from '@catalyst-psa/shared';
import { AgreementFormModal } from './AgreementFormModal.js';
import { ProrateInvoiceModal } from './ProrateInvoiceModal.js';
import { AgreementHoursTab } from './AgreementHoursTab.js';
import { AgreementPortalBillingTab } from './AgreementPortalBillingTab.js';
import { AgreementLinesSection } from './AgreementLinesSection.js';
import { AgreementRatesSection } from './AgreementRatesSection.js';
import { AgreementTimeEntriesSection } from './AgreementTimeEntriesSection.js';
import { AgreementInvoicesTab, AgreementTicketsTab } from './AgreementRelatedTabs.js';
import {
  useAgreement,
  useDisableAgreementPublicLink,
  useRegenerateAgreementPublicLink,
} from './api.js';
import { AGREEMENT_STATUS_LABELS, AGREEMENT_STATUS_TONES } from './status.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

interface DetailItemProps {
  label: string;
  value: string;
}

function DetailItem({ label, value }: DetailItemProps): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

export function AgreementDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { can } = useAuth();
  const canEdit = can('agreements', 'edit');
  const navigate = useNavigate();
  const backNav = useBackNavigation();
  const { inOverlay } = useDetailOverlay();

  const agreementQuery = useAgreement(id);

  const [editOpen, setEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [linkCopied, setLinkCopied] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [prorateOpen, setProrateOpen] = useState(false);

  const regenerateLink = useRegenerateAgreementPublicLink();
  const disableLink = useDisableAgreementPublicLink();

  const agreement = agreementQuery.data;
  useTrackRecent(
    agreement
      ? {
          entity: 'agreement',
          id: agreement.id,
          label: agreement.name,
          path: `/agreements/${agreement.id}`,
        }
      : null,
  );

  const customerName = agreement?.customerName ?? '—';

  if (agreementQuery.isLoading || agreementQuery.isError || !agreement) {
    return (
      <div>
        <PageHeader title="Agreement" />
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <DataState
            isLoading={agreementQuery.isLoading}
            isError={agreementQuery.isError}
            error={agreementQuery.error}
            onRetry={() => void agreementQuery.refetch()}
            isEmpty={!agreementQuery.isLoading && !agreement}
            emptyTitle="Agreement not found"
            emptyDescription="This agreement may have been deleted."
          >
            <div />
          </DataState>
        </div>
      </div>
    );
  }

  const isManagedServices = agreement.type === 'managed_services';
  const canProrate =
    agreement.type === 'managed_services' || agreement.type === 'product_subscription';
  /** Portal billing only applies to Managed Services and ConnectBooster subscriptions. */
  const showPortalBilling =
    agreementPortalBillingMode(agreement.type, agreement.subtypeName) !== 'none';
  const portalTab = showPortalBilling ? [{ id: 'portal', label: 'Portal Billing' }] : [];

  return (
    <div>
      {backNav.canGoBack ? (
        <button
          onClick={backNav.goBack}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← {backNav.backLabel}
        </button>
      ) : inOverlay ? null : (
        <Link
          to="/agreements"
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← Back to agreements
        </Link>
      )}

      <PageHeader
        title={agreement.name}
        description={customerName}
        actions={
          <div className="flex items-center gap-2">
            {agreement.customerId ? (
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    `/customers?returnCustomerId=${agreement.customerId}&returnTab=agreements`,
                  )
                }
              >
                Go to Customer
              </Button>
            ) : null}
            {canEdit ? (
              <>
                {canProrate ? (
                  <Button variant="secondary" onClick={() => setProrateOpen(true)}>
                    Generate prorate invoice
                  </Button>
                ) : null}
                <Button variant="secondary" onClick={() => setEditOpen(true)}>
                  Edit agreement
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      {isManagedServices ? (
        <TabNav
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'rates', label: 'Service category rates' },
            { id: 'time', label: 'Time entries' },
            { id: 'tickets', label: 'Tickets' },
            { id: 'invoices', label: 'Invoices' },
            { id: 'hours', label: 'Cat+ Usage' },
            ...portalTab,
            { id: 'history', label: 'History' },
          ]}
          activeId={activeTab}
          onChange={setActiveTab}
        />
      ) : (
        <TabNav
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'rates', label: 'Service category rates' },
            { id: 'time', label: 'Time entries' },
            { id: 'tickets', label: 'Tickets' },
            { id: 'invoices', label: 'Invoices' },
            ...portalTab,
            { id: 'history', label: 'History' },
          ]}
          activeId={activeTab}
          onChange={setActiveTab}
        />
      )}

      {activeTab === 'overview' ? (
        <>
          <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Status</dt>
                <dd className="mt-1">
                  <Badge tone={AGREEMENT_STATUS_TONES[agreement.status]}>
                    {AGREEMENT_STATUS_LABELS[agreement.status]}
                  </Badge>
                </dd>
              </div>
              <DetailItem label="Type" value={AGREEMENT_TYPE_LABELS[agreement.type]} />
              <DetailItem label="Start date" value={formatDate(agreement.startDate)} />
              <DetailItem label="End date" value={formatDate(agreement.endDate)} />
              <DetailItem label="Customer PO" value={agreement.customerPo ?? '—'} />
              <DetailItem label="Last invoice date" value={formatDate(agreement.lastInvoiceDate)} />
              <DetailItem label="Next invoice date" value={formatDate(agreement.nextInvoiceDate)} />
              <DetailItem
                label="Next invoice amount"
                value={
                  agreement.nextInvoiceAmount
                    ? currency.format(Number(agreement.nextInvoiceAmount))
                    : '—'
                }
              />
              <DetailItem
                label="Rate increase date"
                value={formatDate(agreement.rateIncreaseDate)}
              />
              <DetailItem
                label="Rate increase %"
                value={agreement.rateIncreasePercent ? `${agreement.rateIncreasePercent}%` : '—'}
              />
            </dl>
            {isManagedServices ? (
              <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-4">
                <DetailItem label="Hours included" value={agreement.hoursIncluded ?? '—'} />
                {agreement.firstMonthHoursIncluded ? (
                  <DetailItem
                    label="First-month hours (prorated)"
                    value={agreement.firstMonthHoursIncluded}
                  />
                ) : null}
                <DetailItem label="Allow rollover" value={agreement.allowRollover ? 'Yes' : 'No'} />
                <DetailItem label="Hours cap" value={agreement.hoursCap ?? '—'} />
                <DetailItem label="Overage rate" value={agreement.overageRate ?? '—'} />
                <DetailItem label="Rollover bank" value={agreement.rolloverHours ?? '—'} />
                <DetailItem
                  label="Overage billed thru"
                  value={formatDate(agreement.overageBilledThrough)}
                />
              </dl>
            ) : null}
          </div>

          <div className="space-y-6">
            {agreement.internalNotes?.trim() ? (
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-xs uppercase tracking-wide text-slate-500">Internal notes</h2>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                  {agreement.internalNotes}
                </p>
              </div>
            ) : null}

            <AgreementLinesSection
              agreementId={agreement.id}
              canEdit={canEdit}
              showCatPlus={agreement.type === 'managed_services'}
            />
          </div>
        </>
      ) : null}

      {activeTab === 'rates' ? (
        <AgreementRatesSection agreementId={agreement.id} canEdit={canEdit} />
      ) : null}

      {activeTab === 'time' ? <AgreementTimeEntriesSection agreementId={agreement.id} /> : null}

      {activeTab === 'tickets' ? <AgreementTicketsTab agreementId={agreement.id} /> : null}

      {activeTab === 'invoices' ? <AgreementInvoicesTab agreementId={agreement.id} /> : null}

      {activeTab === 'hours' ? (
        <div className="space-y-6">
          <AgreementUsageLinkCard
            publicToken={agreement.publicToken}
            canEdit={canEdit}
            linkCopied={linkCopied}
            onCopy={(url) => {
              void navigator.clipboard.writeText(url).then(() => {
                setLinkCopied(true);
                window.setTimeout(() => setLinkCopied(false), 2000);
              });
            }}
            onGenerate={() => regenerateLink.mutate(agreement.id)}
            isGenerating={regenerateLink.isPending}
            onRegenerate={() => setRegenerateOpen(true)}
            onDisable={() => setDisableOpen(true)}
          />
          <AgreementHoursTab agreementId={agreement.id} canEdit={canEdit} />
        </div>
      ) : null}

      {activeTab === 'portal' && showPortalBilling ? (
        <AgreementPortalBillingTab agreementId={agreement.id} canEdit={canEdit} />
      ) : null}

      {activeTab === 'history' ? (
        <HistoryPanel entityType="Agreement" entityId={agreement.id} />
      ) : null}

      <AgreementFormModal
        isOpen={editOpen}
        agreement={agreement}
        onClose={() => setEditOpen(false)}
        onDeleted={() => navigate('/agreements')}
      />

      <ProrateInvoiceModal
        isOpen={prorateOpen}
        agreementId={agreement.id}
        onClose={() => setProrateOpen(false)}
        onCreated={(invoice) => {
          setProrateOpen(false);
          navigate(`/invoices/${invoice.id}`);
        }}
      />

      <ConfirmDialog
        isOpen={regenerateOpen}
        title="Regenerate public link"
        message="Generate a new public usage link for this agreement? The current link will stop working immediately and anyone using it will need the new one."
        confirmLabel="Regenerate link"
        destructive
        isConfirming={regenerateLink.isPending}
        onConfirm={() => {
          regenerateLink.mutate(agreement.id, { onSuccess: () => setRegenerateOpen(false) });
        }}
        onCancel={() => setRegenerateOpen(false)}
      />

      <ConfirmDialog
        isOpen={disableOpen}
        title="Disable public link"
        message="Disable the public usage link for this agreement? The current link will stop working immediately."
        confirmLabel="Disable link"
        destructive
        isConfirming={disableLink.isPending}
        onConfirm={() => {
          disableLink.mutate(agreement.id, { onSuccess: () => setDisableOpen(false) });
        }}
        onCancel={() => setDisableOpen(false)}
      />
    </div>
  );
}

interface AgreementUsageLinkCardProps {
  publicToken: string | null;
  canEdit: boolean;
  linkCopied: boolean;
  onCopy: (url: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  onRegenerate: () => void;
  onDisable: () => void;
}

/** Card at the top of the Cat+ Usage tab for the public, token-gated usage link. */
function AgreementUsageLinkCard({
  publicToken,
  canEdit,
  linkCopied,
  onCopy,
  onGenerate,
  isGenerating,
  onRegenerate,
  onDisable,
}: AgreementUsageLinkCardProps): JSX.Element {
  if (!publicToken) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800">Public usage link</p>
            <p className="mt-1 text-xs text-slate-400">
              Share a read-only view of this Cat+ usage with the customer. No sign-in required.
            </p>
          </div>
          {canEdit ? (
            <Button variant="secondary" onClick={onGenerate} disabled={isGenerating}>
              {isGenerating ? 'Generating…' : 'Generate link'}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/a/${publicToken}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800">Public usage link</p>
          <p className="mt-1 truncate font-mono text-xs text-slate-500">{publicUrl}</p>
          <p className="mt-1 text-xs text-slate-400">
            Anyone with this link can view this agreement's Cat+ usage. No sign-in required.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={() => onCopy(publicUrl)}>
            {linkCopied ? 'Copied!' : 'Copy link'}
          </Button>
          {canEdit ? (
            <>
              <Button variant="ghost" onClick={onRegenerate}>
                Regenerate
              </Button>
              <Button variant="ghost" onClick={onDisable}>
                Disable
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
